import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import LuxuryInput from '../../components/ui/LuxuryInput';
import LuxuryLoading from '../../components/ui/LuxuryLoading';
import { FaArrowRight } from 'react-icons/fa';
import { fadeInUp, pageTransition } from "../../components/animations/animations";
import { AuthService } from "../../api/auth.api";

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        remember: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const hasCheckedAuth = useRef(false);
    const redirectAttempted = useRef(false);

    // Check if user is already logged in
    useEffect(() => {
        // Prevent multiple checks
        if (hasCheckedAuth.current) {
            setIsCheckingAuth(false);
            return;
        }

        const checkExistingAuth = async () => {
            try {
                console.log('Login: Checking existing authentication...');
                hasCheckedAuth.current = true;

                // First, check if we have any stored user data
                const storedUser = AuthService.getStoredUser();

                if (!storedUser) {
                    console.log('Login: No stored user found');
                    setIsCheckingAuth(false);
                    return;
                }

                // Check if token is expired
                if (AuthService.isTokenExpired()) {
                    console.log('Login: Token expired, attempting refresh...');
                    try {
                        await AuthService.refreshToken();
                        console.log('Login: Token refreshed successfully');
                    } catch (refreshError) {
                        console.log('Login: Token refresh failed:', refreshError);
                        // Clear invalid tokens
                        AuthService.logout();
                        setIsCheckingAuth(false);
                        return;
                    }
                }

                // Verify authentication with server
                console.log('Login: Verifying authentication with server...');
                const authResult = await AuthService.checkAuth();

                if (authResult.authenticated) {
                    console.log('Login: User already authenticated, redirecting...');

                    // Update last active timestamp
                    AuthService.updateLastActive();

                    // Handle redirect
                    handleRedirect(true);
                } else {
                    console.log('Login: Server authentication failed');
                    // Clear invalid session
                    AuthService.logout();
                    setIsCheckingAuth(false);
                }

            } catch (error) {
                console.error('Login: Authentication check error:', error);
                // Not authenticated, stay on login page
                setIsCheckingAuth(false);
            }
        };

        checkExistingAuth();

        // Cleanup
        return () => {
            // Don't reset here to prevent re-checks
        };
    }, []); // Removed navigate and location dependencies

    const handleRedirect = (isAutoRedirect = false) => {
        // Prevent multiple redirect attempts
        if (redirectAttempted.current) {
            console.log('Login: Redirect already attempted, skipping...');
            return;
        }

        redirectAttempted.current = true;

        try {
            // Get redirect path from storage or location state
            let redirectPath = null;

            // Check session storage
            const sessionRedirect = sessionStorage.getItem('kromaic_redirect_after_login');
            console.log('Login: Session redirect:', sessionRedirect);

            // Check local storage
            const localRedirect = localStorage.getItem('kromaic_redirect_after_login');
            console.log('Login: Local redirect:', localRedirect);

            // Check location state
            const locationState = location.state || {};
            console.log('Login: Location state:', locationState);

            if (sessionRedirect && typeof sessionRedirect === 'string') {
                redirectPath = sessionRedirect;
            } else if (localRedirect && typeof localRedirect === 'string') {
                redirectPath = localRedirect;
            } else if (locationState.redirectTo && typeof locationState.redirectTo === 'string') {
                redirectPath = locationState.redirectTo;
            } else if (locationState.from) {
                // Handle both string and object formats
                if (typeof locationState.from === 'string') {
                    redirectPath = locationState.from;
                } else if (locationState.from && typeof locationState.from === 'object' && locationState.from.pathname) {
                    redirectPath = locationState.from.pathname;
                }
            }

            console.log('Login: Raw redirect path:', redirectPath);

            // If no redirect path found, default to /account
            // But check if we're already going to /account to prevent loops
            if (!redirectPath ||
                typeof redirectPath !== 'string' ||
                redirectPath === '/login' ||
                redirectPath === window.location.pathname) {

                console.log('Login: No valid redirect path found, defaulting to /account');
                redirectPath = '/account';
            }

            // Clean up the path
            redirectPath = redirectPath.trim();

            // Ensure path starts with /
            if (!redirectPath.startsWith('/')) {
                redirectPath = '/' + redirectPath;
            }

            console.log('Login: Cleaned redirect path:', redirectPath);

            // Clear redirect storage
            sessionStorage.removeItem('kromaic_redirect_after_login');
            localStorage.removeItem('kromaic_redirect_after_login');

            // Use a small delay to ensure React state updates
            setTimeout(() => {
                console.log('Login: Navigating to:', redirectPath);
                navigate(redirectPath, { replace: true });
            }, 100);

        } catch (redirectError) {
            console.error('Login: Redirect error:', redirectError);
            // Fallback to account page
            setTimeout(() => {
                navigate('/account', { replace: true });
            }, 100);
        }
    };

    // In Login.jsx - update handleSubmit function
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setIsLoading(true);

        try {
            console.log('Login: Attempting login with:', formData.email);

            const res = await AuthService.login(
                formData.email,
                formData.password,
                formData.remember
            );

            console.log('Login: Successful, user:', res.user);

            // Update last active timestamp
            AuthService.updateLastActive();

            // Check if we have saved checkout data
            const savedCheckoutData = localStorage.getItem('kromaic_checkout_recovery');
            const isFromCheckout = location.state?.returnToCheckout ||
                location.state?.from === '/checkout' ||
                location.state?.source === 'checkout';

            if (isFromCheckout || savedCheckoutData) {
                console.log('Login: User is returning to checkout with saved data');

                // Show success message
                setSuccessMessage('Login successful! Returning to checkout...');

                // Mark that we're coming from successful login (for register flow)
                localStorage.setItem('kromaic_from_login', 'true');

                // Redirect to checkout with a small delay
                setTimeout(() => {
                    navigate('/checkout', {
                        state: {
                            restoreCheckout: true,
                            fromLogin: true
                        },
                        replace: true
                    });
                }, 500);
            } else {
                // Normal login flow
                setSuccessMessage('Login successful! Redirecting...');
                setTimeout(() => {
                    handleRedirect();
                }, 500);
            }

        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || "Invalid email or password. Please try again.");
            setFormData(prev => ({ ...prev, password: '' }));
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading while checking authentication
    if (isCheckingAuth) {
        return <LuxuryLoading message="Checking your session..." />;
    }

    return (
        <motion.div
            variants={pageTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12"
        >
            <div className="container-luxury max-w-6xl mx-auto px-4">
                {/* Hero Section */}
                <AccountHeroBit
                    title="Welcome back"
                    subtitle="Continue your KROMAIC ritual with us"
                    background="champagne"
                >
                    <motion.p
                        variants={fadeInUp}
                        className="text-charcoal/60 text-base"
                    >
                        Access your personalized beauty journey, order history, and exclusive offers.
                    </motion.p>
                </AccountHeroBit>

                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left: Login Form */}
                    <motion.div
                        variants={fadeInUp}
                        className="bg-white rounded-3xl shadow-luxury p-8 md:p-12"
                    >
                        <h2 className="font-heading text-2xl font-medium text-charcoal mb-2">
                            Sign in to your account
                        </h2>
                        <p className="text-charcoal/50 mb-8">
                            Enter your details to continue
                        </p>

                        {successMessage && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-3 bg-sage/10 border border-sage/20 rounded-xl mb-6"
                            >
                                <p className="text-sage text-sm">{successMessage}</p>
                            </motion.div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <LuxuryInput
                                type="email"
                                label="Email Address"
                                placeholder="Enter your email"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({ ...formData, email: e.target.value })
                                }
                                required
                                size="large"
                            />

                            <LuxuryInput
                                type="password"
                                label="Password"
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={(e) =>
                                    setFormData({ ...formData, password: e.target.value })
                                }
                                required
                                size="large"
                            />

                            <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.remember}
                                        onChange={(e) => setFormData({...formData, remember: e.target.checked})}
                                        className="w-4 h-4 rounded border-nude text-champagne focus:ring-champagne/30"
                                    />
                                    <span className="text-sm text-charcoal/70">Remember me</span>
                                </label>

                                <Link
                                    to="/forgot-password"
                                    className="text-sm text-champagne hover:text-champagne/80 transition-colors"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-3 bg-error/10 border border-error/20 rounded-xl"
                                >
                                    <p className="text-error text-sm">{error}</p>
                                </motion.div>
                            )}

                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <button
                                    type="submit"
                                    disabled={isLoading || !formData.email || !formData.password}
                                    className={`
                                        w-full px-8 py-4 rounded-full
                                        bg-gradient-to-r from-champagne via-champagne/90 to-champagne/80
                                        text-charcoal font-medium text-lg
                                        hover:from-champagne/90 hover:via-champagne/80 hover:to-champagne/70
                                        active:scale-95
                                        transition-all duration-300
                                        shadow-lg hover:shadow-xl
                                        disabled:opacity-50 disabled:cursor-not-allowed
                                        relative overflow-hidden group
                                    `}
                                >
                                    <span className="relative flex items-center justify-center gap-3">
                                        {isLoading ? (
                                            <>
                                                <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                                Signing in...
                                            </>
                                        ) : (
                                            <>
                                                <span>Sign In</span>
                                                <span className="group-hover:translate-x-1 transition-transform">
                                                    <FaArrowRight className="w-5 h-5"/>
                                                </span>
                                            </>
                                        )}
                                    </span>

                                    {/* Shimmer effect */}
                                    <span
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>
                                </button>
                            </motion.div>

                            <p className="text-center text-charcoal/50 text-sm">
                                Don't have an account?{' '}
                                <Link
                                    to="/register"
                                    state={{
                                        from: location.state?.from || '/checkout',
                                        source: 'checkout',
                                        preserveCheckout: true
                                    }}
                                    className="text-champagne hover:text-champagne/80 transition-colors font-medium"
                                >
                                    Create account
                                </Link>
                            </p>
                        </form>
                    </motion.div>

                    {/* Right: Benefits */}
                    <motion.div
                        variants={fadeInUp}
                        transition={{ delay: 0.2 }}
                        className="space-y-6"
                    >
                        {[
                            {
                                title: 'Order History',
                                description: 'Track all your purchases and re-order favorites',
                                color: 'from-sage/10 to-sage/5'
                            },
                            {
                                title: 'Wishlist',
                                description: 'Save products for future consideration',
                                color: 'from-champagne/10 to-champagne/5'
                            },
                            {
                                title: 'Personalized Recommendations',
                                description: 'Get curated product suggestions based on your preferences',
                                color: 'from-nude/20 to-nude/10'
                            },
                            {
                                title: 'Exclusive Offers',
                                description: 'Access members-only promotions and early sales',
                                color: 'from-sage/5 to-transparent'
                            }
                        ].map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * index }}
                                whileHover={{ x: 4 }}
                                className={`p-6 rounded-2xl bg-gradient-to-r ${benefit.color} border border-white/50`}
                            >
                                <h3 className="font-medium text-charcoal mb-2">{benefit.title}</h3>
                                <p className="text-sm text-charcoal/60">{benefit.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
};

export default Login;